import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, Users, Layers, Activity, Lock, RefreshCw } from 'lucide-react';
import { getRolesApi, deleteRoleApi } from '../../services/roleApi';
import CreateRoleModal from './CreateRoleModal';

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await getRolesApi();
      if (res.success && Array.isArray(res.data)) {
        setRoles(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setRoleToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (role) => {
    setRoleToEdit(role);
    setIsModalOpen(true);
  };

  const handleDeleteRole = async (role) => {
    if (role.isSystemRole) {
      alert('System roles cannot be deleted.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete role '${role.role_name}'?`)) return;

    try {
      await deleteRoleApi(role._id);
      fetchRoles();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete role');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            <h1 className="text-xl font-bold text-slate-800">Role & Permissions Management</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Configure system roles, hierarchy levels, module permissions &amp; pipeline stage access controls
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchRoles}
            className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            title="Refresh Roles"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={handleOpenCreate}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-200 flex items-center gap-2 transition"
          >
            <Plus className="w-4 h-4" /> Create Custom Role
          </button>
        </div>
      </div>

      {/* Role Cards Grid */}
      {loading && roles.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-xs">Loading roles...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map(role => {
            const permCount = role.permissions?.length || 0;
            const apptStagesCount = (role.pipelinePermissions?.appointment?.viewStages?.length || 0) + (role.pipelinePermissions?.appointment?.updateStages?.length || 0);
            const surgStagesCount = (role.pipelinePermissions?.surgical?.viewStages?.length || 0) + (role.pipelinePermissions?.surgical?.updateStages?.length || 0);

            return (
              <div
                key={role._id || role.role_name}
                className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition p-5 flex flex-col justify-between"
              >
                <div>
                  {/* Top Bar */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-slate-800">{role.role_name}</h3>
                        {role.isSystemRole && (
                          <span className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                            System
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {role.description || 'No description provided'}
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEdit(role)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-slate-50 transition"
                        title="Edit Role Permissions"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!role.isSystemRole && (
                        <button
                          onClick={() => handleDeleteRole(role)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                          title="Delete Role"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Badges & Metrics */}
                  <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
                    <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                      <div className="text-[10px] uppercase font-bold text-slate-400">Hierarchy Level</div>
                      <div className="text-sm font-extrabold text-slate-800">Rank #{role.hierarchyLevel || 50}</div>
                    </div>

                    <div className="bg-indigo-50/50 p-2.5 rounded-xl border border-indigo-100/50">
                      <div className="text-[10px] uppercase font-bold text-indigo-400">Members</div>
                      <div className="text-sm font-extrabold text-indigo-900 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> {role.memberCount || 0} Staff
                      </div>
                    </div>
                  </div>

                  {/* Permission Summary Pill List */}
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span className="flex items-center gap-1 font-semibold">
                        <Lock className="w-3.5 h-3.5 text-slate-400" /> Module Permissions:
                      </span>
                      <span className="font-bold text-slate-800">{permCount} Granted</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span className="flex items-center gap-1 font-semibold">
                        <Activity className="w-3.5 h-3.5 text-blue-500" /> Appointment Stages:
                      </span>
                      <span className="font-bold text-blue-700">{apptStagesCount} Stage Rules</span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span className="flex items-center gap-1 font-semibold">
                        <Layers className="w-3.5 h-3.5 text-emerald-500" /> Surgical Stages:
                      </span>
                      <span className="font-bold text-emerald-700">{surgStagesCount} Stage Rules</span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center justify-between">
                  <span>ID: {role._id ? role._id.slice(-6) : 'system'}</span>
                  <button
                    onClick={() => handleOpenEdit(role)}
                    className="font-bold text-indigo-600 hover:text-indigo-800"
                  >
                    Manage Access &rarr;
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <CreateRoleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        roleToEdit={roleToEdit}
        onSuccess={fetchRoles}
      />
    </div>
  );
}
